import axios from 'axios';
import {
  API_URL, setToken, setUser, getRefreshToken, getRefreshUser, clearRefreshToken,
} from '../variables/constants';
import { entetesAppareil } from '../utils/infoAppareil';

/**
 * Renouvellement silencieux de la session - ce qui fait vivre « Se souvenir de
 * moi ».
 *
 * <p>Module a part, et non une methode d'`authService` : l'intercepteur de
 * `axiosInstance` doit pouvoir l'appeler sur un 401, or `authService` tire
 * `navigationService`, qui tire `axiosInstance`. Le cycle serait boucle. Ce
 * fichier ne depend que d'axios brut et des constantes.
 *
 * <p>Le jeton de rafraichissement ne donne acces a aucune ressource :
 * `JwtAuthFilter` refuse un jeton de type `refresh` partout ailleurs. Il ne sert
 * qu'ici, ou le serveur revalide la session, le compte et l'appareil avant
 * d'emettre un nouvel acces.
 */

/**
 * Un seul renouvellement a la fois.
 *
 * <p>Un ecran charge dix appels en parallele : au retour, dix 401 arrivent
 * ensemble. Sans ce partage, on demanderait dix renouvellements simultanes avec
 * le meme jeton - le serveur en honorerait un et les neuf autres verraient un
 * jeton deja consomme, ce qui deconnecterait l'utilisateur juste apres l'avoir
 * reconnecte.
 */
let enCours = null;

/**
 * @returns {Promise<boolean>} vrai si un nouvel acces a ete obtenu.
 *   N'echoue jamais : un jeton expire, revoque ou lie a un appareil retire est
 *   un cas NORMAL - on rend `false` et l'appelant retombe sur la connexion.
 */
export const renouvelerSilencieusement = () => {
  if (enCours) return enCours;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);

  enCours = axios
    .post(`${API_URL}/v1/auth/renouveler-token`, { refreshToken }, {
      withCredentials: true,
      headers: entetesAppareil(),
    })
    .then((res) => {
      const data = res.data?.data ?? res.data;
      const accessToken = data?.access_token;
      if (!accessToken) { clearRefreshToken(); return false; }
      // L'utilisateur AVANT le jeton : `setToken` reveille ses abonnes (STOMP,
      // AppelProvider), qui lisent l'utilisateur dans la foulee.
      const utilisateur = getRefreshUser();
      if (utilisateur) setUser(utilisateur);
      setToken(accessToken);
      return true;
    })
    .catch((err) => {
      // Ne jeter le jeton que si le serveur a REFUSE la session. Un echec reseau,
      // un serveur arrete ou un 502 ne prouvent rien sur sa validite : les traiter
      // comme un refus supprimait « Se souvenir de moi » au premier incident, et
      // l'utilisateur ressaisissait son mot de passe sans jamais savoir pourquoi.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // La session est morte - revoquee, expiree, ou appareil retire. Garder le
        // jeton relancerait un renouvellement voue a echouer a chaque requete.
        clearRefreshToken();
      }
      return false;
    })
    .finally(() => { enCours = null; });

  return enCours;
};
